/**
 * Profile Module
 * Handles user profile management, image uploads, and profile updates
 */
import { ProfileAPI, apiRequest } from '../services/api.js';
import { AppState, updateState, VERSION, _isDev, normalizeImageUrl } from '../config.js';
import { showToast, Utils } from '../utils/ui.js';
import { t } from '../utils/i18n.js';
import { isPushSupported, subscribeToPush, unsubscribeFromPush } from '../services/push.js';
import { haptic } from '../utils/haptics.js';

// ── Image processing constants ────────────────────────────────────────────────
const IMAGE_MAX_WIDTH    = 1024;          // output width cap (px)
const IMAGE_MAX_HEIGHT   = 1280;          // output height cap for 4:5 (1024 × 5/4)
const IMAGE_JPEG_QUALITY = 0.85;
const IMAGE_SIZE_THRESHOLD = 4 * 1024 * 1024; // 4 MB — resize even if dimensions are ok
const CROP_W = 4;                         // target crop ratio: 4:5 portrait (matches swipe card)
const CROP_H = 5;
const IMAGE_CROP_TOLERANCE = 0.05;        // allow ±5% deviation before showing crop UI

// ── Crop modal state ──────────────────────────────────────────────────────────
let _cropResolve  = null;
let _cropReject   = null;
let _cropImg      = null;   // loaded HTMLImageElement
let _cropObjUrl   = null;   // object URL to revoke on close
let _cropBoxX     = 0;      // crop selection box position in display px (relative to image top-left)
let _cropBoxY     = 0;
let _cropBoxW     = 0;      // crop box width in display px  (4 parts)
let _cropBoxH     = 0;      // crop box height in display px (5 parts)
let _cropImgDispW = 0;      // displayed image width in px
let _cropImgDispH = 0;      // displayed image height in px
let _cropScaleX   = 1;      // source pixels per display pixel

// ── Image processing helpers ──────────────────────────────────────────────────

/** Load a File into a fully-resolved HTMLImageElement */
function loadImageFromFile(file) {
    return new Promise((resolve, reject) => {
        const url = URL.createObjectURL(file);
        const img = new Image();
        img.onload  = () => { URL.revokeObjectURL(url); resolve(img); };
        img.onerror = () => { URL.revokeObjectURL(url); reject(new Error('Image load failed')); };
        img.src = url;
    });
}

/** Scale img down so it fits within IMAGE_MAX_WIDTH × IMAGE_MAX_HEIGHT, preserving aspect ratio */
function resizeImageToCanvas(img) {
    const scale = Math.min(1, IMAGE_MAX_WIDTH / img.naturalWidth, IMAGE_MAX_HEIGHT / img.naturalHeight);
    const w = Math.round(img.naturalWidth  * scale);
    const h = Math.round(img.naturalHeight * scale);
    const canvas = document.createElement('canvas');
    canvas.width  = w;
    canvas.height = h;
    canvas.getContext('2d').drawImage(img, 0, 0, w, h);
    return canvas;
}

/** Promise wrapper for canvas.toBlob — always outputs JPEG */
function canvasToBlob(canvas) {
    return new Promise(resolve => canvas.toBlob(resolve, 'image/jpeg', IMAGE_JPEG_QUALITY));
}

// ── Crop UI helpers ───────────────────────────────────────────────────────────

function _updateCropProgress(current, total) {
    const el = document.getElementById('crop-progress-text');
    if (!el) return;
    if (total > 1) {
        el.textContent = t('profile.cropProgress', { current, total });
        el.classList.remove('hidden');
    } else {
        el.classList.add('hidden');
    }
}

/** Position the crop selection box on screen */
function _applyCropBox(cropBox) {
    cropBox.style.left   = `${_cropBoxX}px`;
    cropBox.style.top    = `${_cropBoxY}px`;
    cropBox.style.width  = `${_cropBoxW}px`;
    cropBox.style.height = `${_cropBoxH}px`;
}

/** Drag the crop box within the image bounds */
function _setupCropBoxDrag(cropBox) {
    let isDragging = false;
    let startX = 0, startY = 0, startBoxX = 0, startBoxY = 0;

    function getPoint(e) {
        return e.type.startsWith('touch') ? e.touches[0] : e;
    }

    function onStart(e) {
        e.preventDefault();
        isDragging = true;
        const pt = getPoint(e);
        startX = pt.clientX; startY = pt.clientY;
        startBoxX = _cropBoxX; startBoxY = _cropBoxY;
        document.addEventListener('mousemove', onMove);
        document.addEventListener('touchmove', onMove, { passive: false });
        document.addEventListener('mouseup', onEnd);
        document.addEventListener('touchend', onEnd);
    }

    function onMove(e) {
        if (!isDragging) return;
        e.preventDefault();
        const pt = getPoint(e);
        _cropBoxX = Math.max(0, Math.min(_cropImgDispW - _cropBoxW, startBoxX + pt.clientX - startX));
        _cropBoxY = Math.max(0, Math.min(_cropImgDispH - _cropBoxH, startBoxY + pt.clientY - startY));
        _applyCropBox(cropBox);
    }

    function onEnd() {
        isDragging = false;
        document.removeEventListener('mousemove', onMove);
        document.removeEventListener('touchmove', onMove);
        document.removeEventListener('mouseup', onEnd);
        document.removeEventListener('touchend', onEnd);
    }

    // Remove previous listeners (handles re-use across multiple files)
    if (cropBox._cropBoxStart) {
        cropBox.removeEventListener('mousedown', cropBox._cropBoxStart);
        cropBox.removeEventListener('touchstart', cropBox._cropBoxStart);
    }
    cropBox._cropBoxStart = onStart;
    cropBox.addEventListener('mousedown', onStart);
    cropBox.addEventListener('touchstart', onStart, { passive: false });
}

function _initCropUI(img, file, fileIndex, totalFiles) {
    _cropImg = img;
    const previewImg = document.getElementById('crop-preview-img');
    const container  = document.getElementById('crop-container');
    const cropBox    = document.getElementById('crop-box');

    // Set preview source
    if (_cropObjUrl) URL.revokeObjectURL(_cropObjUrl);
    _cropObjUrl = URL.createObjectURL(file);
    previewImg.src = _cropObjUrl;

    // Calculate display dimensions: fit image within available width & 55vh height
    container.style.width  = '100%';
    container.style.height = 'auto';
    const availW = container.offsetWidth;
    const maxH   = Math.round(window.innerHeight * 0.55);
    const scale  = Math.min(availW / img.naturalWidth, maxH / img.naturalHeight);

    _cropImgDispW = Math.round(img.naturalWidth  * scale);
    _cropImgDispH = Math.round(img.naturalHeight * scale);
    _cropScaleX   = img.naturalWidth / _cropImgDispW; // source px per display px

    // Size the image and container explicitly
    previewImg.style.width  = `${_cropImgDispW}px`;
    previewImg.style.height = `${_cropImgDispH}px`;
    container.style.width   = `${_cropImgDispW}px`;
    container.style.height  = `${_cropImgDispH}px`;

    // Crop box: largest 4:5 rectangle that fits, centered
    _cropBoxW = Math.min(_cropImgDispW, Math.round(_cropImgDispH * CROP_W / CROP_H));
    _cropBoxH = Math.round(_cropBoxW * CROP_H / CROP_W);
    // If height overflows (very wide image), constrain by height instead
    if (_cropBoxH > _cropImgDispH) {
        _cropBoxH = _cropImgDispH;
        _cropBoxW = Math.round(_cropBoxH * CROP_W / CROP_H);
    }
    _cropBoxX = Math.round((_cropImgDispW - _cropBoxW) / 2);
    _cropBoxY = Math.round((_cropImgDispH - _cropBoxH) / 2);

    _applyCropBox(cropBox);
    _setupCropBoxDrag(cropBox);
    _updateCropProgress(fileIndex, totalFiles);
}

function _closeCropModal() {
    document.getElementById('crop-modal').classList.add('hidden');
    const previewImg = document.getElementById('crop-preview-img');
    if (previewImg) previewImg.src = '';
    if (_cropObjUrl) { URL.revokeObjectURL(_cropObjUrl); _cropObjUrl = null; }
}

/** Open the crop modal and return a Promise that resolves to a Blob or rejects with { cancelled: true } */
function showCropModal(img, file, fileIndex = 1, totalFiles = 1) {
    return new Promise((resolve, reject) => {
        _cropResolve = resolve;
        _cropReject  = reject;
        // Show modal first so layout is calculated, then init UI on next frame
        document.getElementById('crop-modal').classList.remove('hidden');
        requestAnimationFrame(() => _initCropUI(img, file, fileIndex, totalFiles));
    });
}

async function _confirmCrop() {
    const srcX   = Math.round(_cropBoxX * _cropScaleX);
    const srcY   = Math.round(_cropBoxY * _cropScaleX);
    const srcW   = Math.round(_cropBoxW * _cropScaleX);
    const srcH   = Math.round(_cropBoxH * _cropScaleX);
    const outW   = Math.min(IMAGE_MAX_WIDTH,  srcW);
    const outH   = Math.min(IMAGE_MAX_HEIGHT, srcH);

    const canvas = document.createElement('canvas');
    canvas.width  = outW;
    canvas.height = outH;
    canvas.getContext('2d').drawImage(_cropImg, srcX, srcY, srcW, srcH, 0, 0, outW, outH);

    const blob = await canvasToBlob(canvas);
    _closeCropModal();
    if (_cropResolve) { _cropResolve(blob); _cropResolve = null; _cropReject = null; }
}

function _cancelCrop() {
    _closeCropModal();
    if (_cropReject) { _cropReject({ cancelled: true }); _cropResolve = null; _cropReject = null; }
}

/** Process a single file: crop if not 4:5, resize if oversized, re-encode always */
async function processImageForUpload(file, fileIndex = 1, totalFiles = 1) {
    const img = await loadImageFromFile(file);
    const ratio = img.naturalWidth / img.naturalHeight;
    const targetRatio = CROP_W / CROP_H; // 0.8
    const needsCrop = Math.abs(ratio - targetRatio) > IMAGE_CROP_TOLERANCE;

    if (needsCrop) {
        return showCropModal(img, file, fileIndex, totalFiles);
    }

    const needsResize = img.naturalWidth > IMAGE_MAX_WIDTH
        || img.naturalHeight > IMAGE_MAX_HEIGHT
        || file.size > IMAGE_SIZE_THRESHOLD;

    let canvas;
    if (needsResize) {
        canvas = resizeImageToCanvas(img);
    } else {
        canvas = document.createElement('canvas');
        canvas.width  = img.naturalWidth;
        canvas.height = img.naturalHeight;
        canvas.getContext('2d').drawImage(img, 0, 0);
    }
    return canvasToBlob(canvas);
}

/**
 * Load user profile data
 */
export async function loadProfile() {
    try {
        const response = await ProfileAPI.getProfile();
        const user = response.user;

        // Update form fields
        document.getElementById('profile-name').value = user.name || '';
        document.getElementById('profile-bio').value = user.bio || '';

        // Gender
        const profileGenderSelector = document.getElementById('profile-gender-selector');
        if (profileGenderSelector && user.gender) {
            profileGenderSelector.querySelectorAll('.gender-btn').forEach(btn => {
                btn.classList.toggle('selected', btn.getAttribute('data-gender') === user.gender);
            });
        }

        // Birthdate
        const birthdateInput = document.getElementById('profile-birthdate');
        if (birthdateInput && user.birthdate) {
            birthdateInput.value = user.birthdate;
        }

        // Load profile images
        const profileImages = user.profile_images || [];
        const imagesContainer = document.getElementById('profile-images');

        imagesContainer.innerHTML = profileImages.map((imageUrl, index) => `
            <div class="profile-image-item">
                <img src="${normalizeImageUrl(imageUrl)}" alt="Profile ${index + 1}">
                <button type="button" class="delete-btn" onclick="deleteProfileImage('${encodeURIComponent(imageUrl)}')">
                    <i class="fas fa-times"></i>
                </button>
            </div>
        `).join('');

        // Add upload slot if less than 5 images
        if (profileImages.length < 5) {
            imagesContainer.innerHTML += `
                <div class="profile-image-item border-2 border-dashed border-border flex items-center justify-center cursor-pointer hover:border-primary transition-colors"
                     onclick="document.getElementById('image-upload').click()">
                    <i class="fas fa-plus text-2xl text-muted-foreground"></i>
                </div>
            `;
        }

    } catch (error) {
        console.error('Failed to load profile:', error);
        showToast(t('profile.failedLoadProfile'), 'error');
    }

    // Render additional sections
    renderVersionDisplay();
    initResetAppButton();
    renderDevToolsSection();
    initPushSection();
    renderInstallStatus();
}

/**
 * Display app version in profile
 */
function renderVersionDisplay() {
    const el = document.getElementById('profile-version-text');
    if (el) {
        el.textContent = `${t('profile.version')}: ${VERSION}`;
    }
}

/**
 * Render dev tools section (localhost only)
 */
function renderDevToolsSection() {
    const container = document.getElementById('dev-tools-section');
    if (!container) return;

    if (!_isDev) {
        container.classList.add('hidden');
        return;
    }

    container.classList.remove('hidden');

    const killBtn = document.getElementById('dev-kill-sw-btn');
    const refreshBtn = document.getElementById('dev-refresh-status-btn');
    const statusDisplay = document.getElementById('dev-sw-status');

    if (killBtn && !killBtn._bound) {
        killBtn._bound = true;
        killBtn.addEventListener('click', async () => {
            if (confirm(t('profile.killSWConfirm'))) {
                await window.killServiceWorker();
            }
        });
    }

    if (refreshBtn && !refreshBtn._bound) {
        refreshBtn._bound = true;
        refreshBtn.addEventListener('click', async () => {
            const status = await window.getServiceWorkerStatus();
            if (statusDisplay) {
                statusDisplay.textContent = JSON.stringify(status, null, 2);
                statusDisplay.classList.remove('hidden');
            }
        });
    }
}

/**
 * Wire up the Reset App button (visible to all users)
 */
function initResetAppButton() {
    const btn = document.getElementById('profile-reset-app-btn');
    if (!btn || btn._bound) return;
    btn._bound = true;
    btn.addEventListener('click', async () => {
        if (confirm(t('profile.resetAppConfirm'))) {
            await window.killServiceWorker();
        }
    });
}

/**
 * Initialize push notification section in profile
 */
async function initPushSection() {
    const section = document.getElementById('push-section');
    if (!section) return;

    const toggle = document.getElementById('push-toggle');
    const permWarning = document.getElementById('push-permission-warning');
    const testBtn = document.getElementById('push-test-btn');
    const devicesList = document.getElementById('push-devices-list');

    if (!isPushSupported()) {
        section.querySelector('.push-content')?.classList.add('hidden');
        const notSupported = document.getElementById('push-not-supported');
        if (notSupported) notSupported.classList.remove('hidden');
        return;
    }

    // Check current permission state
    const permission = Notification.permission;

    if (permission === 'denied') {
        if (permWarning) permWarning.classList.remove('hidden');
        if (toggle) toggle.disabled = true;
    } else {
        if (permWarning) permWarning.classList.add('hidden');
        if (toggle) toggle.disabled = false;
    }

    // Check if currently subscribed (has token in localStorage)
    const hasToken = !!localStorage.getItem('ss_fcm_token');
    if (toggle) toggle.checked = hasToken && permission === 'granted';

    // Wire toggle
    if (toggle && !toggle._bound) {
        toggle._bound = true;
        toggle.addEventListener('change', async () => {
            haptic('toggle');
            if (toggle.checked) {
                try {
                    const result = await subscribeToPush();
                    if (result) {
                        showToast(t('profile.pushEnabled'), 'success');
                        loadPushDevices(devicesList);
                    } else {
                        toggle.checked = false;
                        showToast(t('profile.pushEnableFailed'), 'error');
                    }
                } catch (err) {
                    toggle.checked = false;
                    showToast(t('profile.pushEnableFailed'), 'error');
                }
            } else {
                try {
                    await unsubscribeFromPush();
                    showToast(t('profile.pushDisabled'), 'info');
                    if (devicesList) devicesList.innerHTML = '';
                } catch (err) {
                    toggle.checked = true;
                    console.error('Failed to unsubscribe:', err);
                }
            }
        });
    }

    // Wire test button
    if (testBtn && !testBtn._bound) {
        testBtn._bound = true;
        testBtn.addEventListener('click', async () => {
            try {
                const result = await apiRequest('/push/test', { method: 'POST' });
                if (result.success && result.sent > 0) {
                    showToast(t('profile.pushTestSent'), 'success', true);
                } else {
                    showToast(t('profile.pushTestFailed'), 'error');
                }
            } catch (err) {
                showToast(t('profile.pushTestFailed'), 'error');
            }
        });
    }

    // Load device list if subscribed
    if (hasToken && permission === 'granted') {
        loadPushDevices(devicesList);
    }
}

/**
 * Load push subscription devices list
 */
async function loadPushDevices(container) {
    if (!container) return;
    try {
        const data = await apiRequest('/push/status');
        if (data.subscriptions && data.subscriptions.length > 0) {
            container.innerHTML = data.subscriptions.map(sub => `
                <div class="flex justify-between items-center py-2 border-b border-gray-100 dark:border-gray-700 last:border-0">
                    <div class="text-sm text-gray-600 dark:text-gray-300 truncate flex-1">
                        <i class="fas fa-mobile-alt mr-2"></i>${sub.user_agent ? sub.user_agent.substring(0, 40) + '...' : 'Device'}
                    </div>
                    <div class="text-xs text-gray-400 ml-2">${sub.updated_at ? Utils.formatRelativeDate(sub.updated_at) : ''}</div>
                </div>
            `).join('');
        } else {
            container.innerHTML = '<p class="text-sm text-gray-400">No registered devices</p>';
        }
    } catch (err) {
        console.error('Failed to load push devices:', err);
    }
}

/**
 * Render install status in profile
 */
function renderInstallStatus() {
    const statusEl = document.getElementById('install-status-text');
    if (!statusEl) return;

    const isStandalone = window.matchMedia('(display-mode: standalone)').matches ||
                         window.navigator.standalone === true;

    if (isStandalone) {
        statusEl.textContent = t('profile.installedStandalone');
        statusEl.className = 'text-sm text-green-500';
    } else {
        statusEl.textContent = t('profile.notInstalled');
        statusEl.className = 'text-sm text-muted-foreground';
    }

    // Show install button if not installed
    const installBtn = document.getElementById('profile-install-btn');
    if (installBtn) {
        if (isStandalone) {
            installBtn.classList.add('hidden');
        } else {
            installBtn.classList.remove('hidden');
        }
    }
}

/**
 * Handle profile form submission
 */
async function handleProfileUpdate(e) {
    e.preventDefault();

    const name = document.getElementById('profile-name').value.trim();
    const bio = document.getElementById('profile-bio').value.trim();
    const genderBtn = document.querySelector('#profile-gender-selector .gender-btn.selected');
    const gender = genderBtn ? genderBtn.getAttribute('data-gender') : undefined;
    const birthdate = document.getElementById('profile-birthdate').value || undefined;

    try {
        const response = await ProfileAPI.updateProfile({ name, bio, gender, birthdate });

        if (response.success) {
            updateState({ currentUser: response.user });
            localStorage.setItem('currentUser', JSON.stringify(response.user));
            showToast(t('profile.profileUpdated'), 'success');
        }

    } catch (error) {
        console.error('Failed to update profile:', error);
        showToast(error.message || t('profile.failedUpdateProfile'), 'error');
    }
}

/**
 * Handle image upload — processes each file (resize/crop) before uploading
 */
async function handleImageUpload(e) {
    const files = Array.from(e.target.files);
    e.target.value = ''; // allow re-selecting the same file after cancel
    if (files.length === 0) return;

    const blobs = [];
    for (let i = 0; i < files.length; i++) {
        const file = files[i];
        if (!file.type.startsWith('image/')) {
            showToast(t('profile.invalidFileType'), 'error');
            continue;
        }
        try {
            blobs.push(await processImageForUpload(file, i + 1, files.length));
        } catch (err) {
            if (err && err.cancelled) continue; // user cancelled crop — skip silently
            console.error('Image processing failed:', err);
            showToast(t('profile.imageProcessFailed'), 'error');
        }
    }

    if (blobs.length === 0) return;

    const formData = new FormData();
    blobs.forEach((blob, i) => formData.append('images', blob, `photo_${i}.jpg`));

    try {
        const response = await ProfileAPI.uploadImages(formData);

        if (response.success) {
            showToast(t('profile.imagesUploaded'), 'success');
            loadProfile();
        } else {
            throw new Error(response.message);
        }
    } catch (error) {
        console.error('Failed to upload images:', error);
        showToast(error.message || t('profile.failedUploadImages'), 'error');
    }
}

/**
 * Delete a profile image
 */
export async function deleteProfileImage(imageUrl) {
    if (!confirm(t('profile.deletePhotoConfirm'))) return;

    try {
        const response = await ProfileAPI.deleteImage(decodeURIComponent(imageUrl));

        if (response.success) {
            showToast(t('profile.imageDeleted'), 'success');
            loadProfile(); // Refresh profile images
        }

    } catch (error) {
        console.error('Failed to delete image:', error);
        showToast(t('profile.failedDeleteImage'), 'error');
    }
}

/**
 * Setup profile event listeners
 */
let profileListenersInitialized = false;

export function setupProfileEventListeners() {
    if (profileListenersInitialized) return;
    profileListenersInitialized = true;

    // Profile form
    const profileForm = document.getElementById('profile-form');
    if (profileForm) {
        profileForm.addEventListener('submit', handleProfileUpdate);
    }

    // Profile gender toggle
    const profileGenderSelector = document.getElementById('profile-gender-selector');
    if (profileGenderSelector) {
        profileGenderSelector.querySelectorAll('.gender-btn').forEach(btn => {
            btn.addEventListener('click', () => {
                profileGenderSelector.querySelectorAll('.gender-btn').forEach(b => b.classList.remove('selected'));
                btn.classList.add('selected');
            });
        });
    }

    // Image upload
    const imageUpload = document.getElementById('image-upload');
    if (imageUpload) {
        imageUpload.addEventListener('change', handleImageUpload);
    }

    // Profile install button
    const installBtn = document.getElementById('profile-install-btn');
    if (installBtn) {
        installBtn.addEventListener('click', () => {
            if (window.triggerInstallPrompt) {
                window.triggerInstallPrompt();
            }
        });
    }
}

// Make functions globally available for HTML onclick handlers
window.deleteProfileImage = deleteProfileImage;
window.confirmCrop = _confirmCrop;
window.cancelCrop  = _cancelCrop;
